import { ConversationStatus } from '@provision/conversation-types';
import { useMemo } from 'react';
import { ConditionInput, ConditionOperator, SortingInput } from 'src/graphql/types';
import { useConversations } from './conversation';
import { Conversation } from './conversation.types';

const DEFAULT_SAME_REQUESTER_LIMIT = 5;

interface UseSameRequesterConversationsParams {
  origin?: Conversation | null;
  enabled?: boolean;
  limit?: number;
}

const SAME_REQUESTER_SORT: SortingInput = {
  attribute: 'lastMessageReceivedAt',
  desc: true,
};

export const useSameRequesterConversations = ({
  origin,
  enabled = true,
  limit = DEFAULT_SAME_REQUESTER_LIMIT,
}: UseSameRequesterConversationsParams) => {
  const requesterIdentity = origin?.requesterData?.id;
  const partnerId = origin?.partnerId;

  const skip = !enabled || !origin || !requesterIdentity || typeof partnerId !== 'number';

  const filter = useMemo<ConditionInput[]>(() => {
    if (skip) {
      return [];
    }

    return [
      {
        attribute: 'partnerId',
        operator: ConditionOperator.Equals,
        value: String(partnerId),
      },
      {
        attribute: 'requester/participantIdentity',
        operator: ConditionOperator.Equals,
        value: requesterIdentity,
      },
      {
        attribute: 'conversationStatusId',
        operator: ConditionOperator.DoesNotEqual,
        value: ConversationStatus.Closed,
      },
      {
        attribute: 'conversationId',
        operator: ConditionOperator.DoesNotEqual,
        value: origin.id,
      },
    ];
  }, [skip, partnerId, requesterIdentity, origin?.id]);

  const { conversations, loading, error } = useConversations({
    filter,
    sort: SAME_REQUESTER_SORT,
    limit,
    skip,
  });

  return {
    conversations: skip ? [] : conversations,
    isLoading: !skip && loading,
    error: skip ? undefined : error,
  };
};
